import { useEffect, useRef, useState } from 'react';
import type { LatLng } from '@mobi/shared';
import { api } from './api.js';
import { Input, Spinner, cx } from './ui.js';

export interface Place {
  id: number;
  name: string;
  kind: string;
  city: string | null;
  neighborhood: string | null;
  street: string | null;
  housenumber: string | null;
  lat: number;
  lng: number;
}

/** Rótulo curto do que é o lugar, para a pessoa distinguir homônimos. */
const KIND_LABEL: Record<string, string> = {
  comercio: 'Comércio',
  servico: 'Serviço',
  lazer: 'Lazer',
  rua: 'Rua',
  localidade: 'Localidade',
  endereco: 'Endereço',
};

export function AddressSearch({
  label,
  placeholder,
  value,
  near,
  onPick,
  onClear,
}: {
  label: string;
  placeholder: string;
  /** endereço já escolhido; some da caixa quando o usuário digita de novo */
  value?: string | null;
  /**
   * Onde quem busca está agora. Usada só para desempate — o servidor
   * favorece resultado perto disto quando o termo é genérico demais para
   * distinguir por nome. Sem isto, tudo desempata pelo centro fixo da
   * área atendida, o que empurra quem está numa cidade menor para o
   * final da lista mesmo quando a opção mais perto dele é outra.
   */
  near?: LatLng | null;
  onPick: (place: Place & LatLng) => void;
  onClear?: () => void;
}) {
  const [term, setTerm] = useState('');
  const [places, setPlaces] = useState<Place[]>([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  /** fecha a lista sem disparar busca ao escolher um item */
  const picking = useRef(false);
  // Ref e não dependência do efeito de busca: o GPS atualiza a cada
  // poucos segundos, e reiniciar o debounce a cada leitura faria a busca
  // nunca dar tempo de completar enquanto o usuário está com o app aberto.
  const nearRef = useRef(near);
  nearRef.current = near;

  useEffect(() => {
    if (picking.current) {
      picking.current = false;
      return;
    }
    const q = term.trim();
    if (q.length < 2) {
      setPlaces([]);
      setBusy(false);
      return;
    }

    /*
     * Espera a digitação parar. Sem isso, "Sempre Internet" dispara
     * dezesseis buscas — e num 3G de interior a resposta da quarta chega
     * depois da décima, deixando a lista mostrando o resultado errado.
     */
    setBusy(true);
    const controller = new AbortController();
    const near = nearRef.current;
    const timer = setTimeout(() => {
      const coords = near ? `&lat=${near.lat}&lng=${near.lng}` : '';
      void api<{ places: Place[] }>(`/api/places?q=${encodeURIComponent(q)}${coords}`, {
        signal: controller.signal,
      })
        .then((res) => {
          setPlaces(res.places);
          setOpen(true);
        })
        .catch(() => setPlaces([]))
        .finally(() => setBusy(false));
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [term]);

  const showing = open && (busy || places.length > 0 || term.trim().length >= 2);

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-muted">
        {label}
        <Input
          className="mt-1"
          placeholder={placeholder}
          value={term || (value ?? '')}
          onChange={(e) => {
            setTerm(e.target.value);
            setOpen(true);
            if (value && onClear) onClear();
          }}
          onFocus={() => setOpen(true)}
        />
      </label>

      {busy && <Spinner className="absolute right-3 top-9 h-4 w-4 text-brand" />}

      {showing && (
        <ul className="absolute inset-x-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-2xl border border-line bg-surface shadow-2xl">
          {places.map((place) => (
            <li key={place.id}>
              <button
                type="button"
                className="w-full px-4 py-3 text-left transition hover:bg-surface-2"
                onClick={() => {
                  picking.current = true;
                  setTerm(place.name);
                  setOpen(false);
                  setPlaces([]);
                  onPick({ ...place });
                }}
              >
                <span className="block truncate font-medium">{place.name}</span>
                <span className="block truncate text-xs text-faint">
                  {[
                    KIND_LABEL[place.kind] ?? place.kind,
                    // Rua já está no nome de um endereço ("Rua X, 123");
                    // repetir aqui só empilharia a mesma informação duas
                    // vezes na mesma linha.
                    place.kind === 'endereco' ? null : place.street,
                    place.neighborhood,
                    place.city,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            </li>
          ))}

          {!busy && places.length === 0 && (
            <li className={cx('px-4 py-3 text-sm text-faint')}>
              Nada encontrado. Toque no mapa para marcar o ponto.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
